import mqtt, { type MqttClient } from "mqtt";

import { presignIotWebsocket } from "./sigv4Ws";
import { fetchIotCredentials } from "../api/iot";

export type MqttStatus = "idle" | "connecting" | "connected" | "error" | "unavailable";

export interface OpsMqttClient {
  status: () => MqttStatus;
  subscribe: (topic: string, handler: (payload: unknown) => void) => () => void;
  disconnect: () => void;
}

/** No-op stub returned when /api/iot/credentials is unavailable (503 in dev). */
function buildStubClient(): OpsMqttClient {
  return {
    status: () => "unavailable",
    subscribe: (_topic, _handler) => () => undefined,
    disconnect: () => undefined,
  };
}

export async function buildOpsMqttClient(): Promise<OpsMqttClient> {
  let creds;
  try {
    creds = await fetchIotCredentials();
  } catch {
    // 503 when IOT_ENDPOINT is unset (dev default) — return the stub so the
    // UI still renders; MQTT-driven invalidation just doesn't fire.
    return buildStubClient();
  }

  let mqttStatus: MqttStatus = "connecting";
  const handlers = new Map<string, Set<(payload: unknown) => void>>();

  const signed = await presignIotWebsocket(creds);
  const client: MqttClient = mqtt.connect(signed, {
    protocol: "wss",
    clientId: `ops-${crypto.randomUUID()}`,
    keepalive: 60,
    reconnectPeriod: 5_000,
  });

  // Circuit breaker: if the socket never establishes (e.g. IoT denies
  // iot:Connect, or the presigned creds expired), mqtt.js otherwise retries
  // forever and floods the console. Give up after a handful of failed attempts
  // and surface "error" — a page reload re-mints creds and starts fresh.
  const MAX_RECONNECTS = 6;
  let reconnectCount = 0;

  client.on("connect", () => {
    mqttStatus = "connected";
    reconnectCount = 0;
  });
  client.on("reconnect", () => {
    reconnectCount += 1;
    if (reconnectCount > MAX_RECONNECTS) {
      mqttStatus = "error";
      client.end(true);
    }
  });
  client.on("error", () => {
    mqttStatus = "error";
  });
  client.on("offline", () => {
    mqttStatus = "idle";
  });
  client.on("message", (topic, payload) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload.toString());
    } catch {
      parsed = { raw: payload.toString() };
    }
    handlers.forEach((set, pattern) => {
      if (matchTopic(pattern, topic)) {
        set.forEach((h) => h(parsed));
      }
    });
  });

  return {
    status: () => mqttStatus,
    subscribe(topic, handler) {
      // "noop" is a magic sentinel — keeps hook call counts stable when id is undefined.
      if (topic === "noop") return () => undefined;

      if (!handlers.has(topic)) {
        handlers.set(topic, new Set());
        client.subscribe(topic, { qos: 0 });
      }
      handlers.get(topic)!.add(handler);
      return () => {
        const set = handlers.get(topic);
        if (!set) return;
        set.delete(handler);
        if (set.size === 0) {
          handlers.delete(topic);
          client.unsubscribe(topic);
        }
      };
    },
    disconnect() {
      client.end(true);
    },
  };
}

/** Translate a "topic-with-+wildcards" pattern into an exact-match check. */
function matchTopic(pattern: string, topic: string): boolean {
  const p = pattern.split("/");
  const t = topic.split("/");
  if (p.length !== t.length && !pattern.endsWith("#")) return false;
  return p.every((seg, i) => seg === "+" || seg === "#" || seg === t[i]);
}
